import { LitElement, html, css, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'

export enum Events {
    onCategoriesSelected = 'onCategoriesSelected',
}

export interface CustomEventPayload {
    [Events.onCategoriesSelected]: string[]
}

@customElement('zaptic-category-picker')
export class CategoryPicker extends LitElement {
    static styles = css`
        :host {
            display: block;
            padding: 0.8rem 1.6rem;
            margin-top: 0.8rem;
        }

        .controls {
            display: flex;
            flex-direction: column;
            gap: 0.8rem;
        }

        .category-picker {
            padding: 0;
            margin: 0;
            display: flex;
            gap: 0.8rem;
            list-style: none;
        }

        .category-picker .category::part(button) {
            border-radius: 100vh;
        }

        .category-picker .category[disabled='true']::part(button) {
            background: var(--purple);
            color: var(--white);
            border: none;
        }

        .category-picker .category[disabled='true']::part(button):hover {
            background: var(--purple);
            color: var(--white);
            border: none;
        }
    `

    @property({ type: Array })
    categories: string[]

    @property({ type: Array })
    selected: string[]

    render() {
        return html`
            <header class="controls">
                <ul class="category-picker">
                    ${[this.ShowAllButton(), ...this.categories.map((name) => this.CategoryButton(name))]}
                </ul>
            </header>
        `
    }

    private ShowAllButton() {
        return this.isShowAllButtonNeeded() ? this.CategoryButton(this.showAllLabel()) : nothing
    }

    private showAllLabel(): string {
        return window.i18n('shared:selectTree.showAll')
    }

    private CategoryButton(name: string) {
        const isShowAllButtonSelected = this.showAllLabel() === name && this.areAllOptionsSelected()
        const isSingleOptionSelected = this.selected.includes(name) && !this.areAllOptionsSelected()
        const selected = isShowAllButtonSelected || isSingleOptionSelected
        return html`
            <li>
                <zaptic-button
                    class="category"
                    size="small"
                    type="${selected ? 'primary' : 'tertiary'}"
                    disabled="${selected}"
                    @click=${() => this.onClickCategory(name)}
                >
                    ${name}
                </zaptic-button>
            </li>
        `
    }

    private onClickCategory(category: string) {
        const wasShowAllClicked = !this.categories.includes(category)
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['onCategoriesSelected']>(Events.onCategoriesSelected, {
                detail: wasShowAllClicked ? this.categories : [category],
            })
        )
    }

    private isShowAllButtonNeeded() {
        return this.categories.length > 1
    }

    private areAllOptionsSelected() {
        return this.categories.length === this.selected.length
    }
}
